// src/modules/tesisat/hooks/useFixtureDraggableText.js
//
// Armatür (LightFixture) etiketlerini (textGroup) armatürün ETRAFINDA, sınırlı
// (dairesel) bir alan içinde sürüklemeyi sağlar. Kullanıcı tip/güç/açıklama
// yazılarını armatürden çok uzaklaşmadan konumlandırabilsin diye.
//
// Pattern: useGroundingDraggableLabels (label'ı parent'tan MAX_DISTANCE içinde
// sürükleme) — burada fixture'a uyarlandı + seçim/taşıma sistemiyle çakışmayı
// önlemek için CAPTURE faz + stopImmediatePropagation ("claim") kullanır.
// (useSelection/useGizmo YASAK mega-hook'lar; onlara dokunmadan claim ile geçilir.)

import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Armatür merkezinden text'in uzaklaşabileceği maksimum (fixture-local birim).
// Default text offseti ~ (40,30) → ~50; 90 makul bir serbestlik verir, "çok uzak" değil.
const MAX_DISTANCE = 90;

export default function useFixtureDraggableText(scene, camera, renderer) {
  const dragRef = useRef(null);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const dom = renderer.domElement;

    const ndcOf = (e) => {
      const r = dom.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1
      );
    };

    // Raycast hit'inden armatür textGroup'unu + armatür group'unu çöz.
    const resolve = (obj) => {
      let n = obj, tg = null, fx = null;
      while (n) {
        if (n.name === 'textGroup') tg = n;
        if (n.userData?.type === 'LightFixture') { fx = n; break; }
        n = n.parent;
      }
      return (tg && fx) ? { tg, fx } : null;
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      const ndc = ndcOf(e);
      const rc = new THREE.Raycaster();
      rc.setFromCamera(ndc, camera);
      const hits = rc.intersectObjects(scene.children, true);
      for (const h of hits) {
        if (h.object.visible === false) continue;
        const found = resolve(h.object);
        if (found) {
          // CLAIM: selection/move/gizmo'nun bu mousedown'ı işlemesini engelle.
          e.stopImmediatePropagation();
          e.preventDefault();
          found.fx.updateMatrixWorld(true);
          const world = new THREE.Vector3(ndc.x, ndc.y, 0).unproject(camera);
          const local = found.fx.worldToLocal(world.clone());
          const offset = new THREE.Vector3().subVectors(local, found.tg.position);
          // labelOffset: textGroup origin'inden GÖRÜNÜR text merkezine (label'lar ~(40,30)
          // offsetli) fixture-local vektör. Kısıtlama bu görünür merkeze (armatür merkezine
          // göre) uygulanmalı; yoksa hareket çemberi text'in ilk noktasına merkezlenir.
          const box = new THREE.Box3().setFromObject(found.tg);
          let labelOffset = new THREE.Vector3();
          if (!box.isEmpty()) {
            const wc = box.getCenter(new THREE.Vector3());
            const lc = found.fx.worldToLocal(wc.clone());
            labelOffset.subVectors(lc, found.tg.position);
          }
          dragRef.current = { tg: found.tg, fx: found.fx, offset, labelOffset, z: found.tg.position.z };
          return;
        }
      }
    };

    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      e.stopImmediatePropagation();
      const ndc = ndcOf(e);
      const world = new THREE.Vector3(ndc.x, ndc.y, 0).unproject(camera);
      const local = d.fx.worldToLocal(world.clone());
      const nx = local.x - d.offset.x;
      const ny = local.y - d.offset.y;
      // Kısıtlamayı GÖRÜNÜR text merkezine (textGroup.position + labelOffset) armatür
      // merkezine (0,0) göre uygula → hareket çemberi armatür merkezli olur.
      let cx = nx + d.labelOffset.x;
      let cy = ny + d.labelOffset.y;
      const dist = Math.hypot(cx, cy);
      if (dist > MAX_DISTANCE) {
        const s = MAX_DISTANCE / dist;
        cx *= s; cy *= s;
      }
      d.tg.position.set(cx - d.labelOffset.x, cy - d.labelOffset.y, d.z);
    };

    const onUp = () => { dragRef.current = null; };

    // CAPTURE faz: bubble'daki selection/gizmo handler'larından ÖNCE çalışsın.
    dom.addEventListener('mousedown', onDown, true);
    dom.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);

    return () => {
      dom.removeEventListener('mousedown', onDown, true);
      dom.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
  }, [scene, camera, renderer]);
}
